import React, { useState } from 'react';
import axios from 'axios';
import { Sliders, Download, Info, Pencil, X } from 'lucide-react';
import { API, authHeaders, fmtErr } from '../../lib/auth';
import { PROFILE_OPTIONS } from '../../mock';
import { Chips, Radios } from './Chips';

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const buildForm = (pr) => ({
  age_min: pr.age_min || 18, age_max: pr.age_max || 99, cities: (pr.cities || []).join(', '),
  looking: pr.looking || '', interests: pr.interests || [], lifestyle: pr.lifestyle || [],
  personality: pr.personality || [], notes: pr.notes || '',
});

const PreferencesView = ({ pr, hasPrefs, onEdit }) => (
  <div className="dash-card" data-testid="preferences-view">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
      <h3 style={{ margin: 0 }}>Matching Preferences</h3>
      <button className="btn btn-red btn-sm" onClick={onEdit} data-testid="edit-preferences-btn"><Pencil size={14} /> Edit Preferences</button>
    </div>
    {!hasPrefs ? (
      <p style={{ color: 'var(--muted)', fontSize: 14 }} data-testid="preferences-empty-note">
        You haven't set your matching preferences yet. Click Edit Preferences to tell us what matters to you.
      </p>
    ) : (
      <div className="pv-facts" style={{ marginTop: 8 }} data-testid="preferences-view-facts">
        <div className="pv-fact"><b>Age Range</b><span>{pr.age_min || 18} – {pr.age_max || 99}</span></div>
        <div className="pv-fact"><b>Preferred Location(s)</b><span>{(pr.cities || []).join(', ') || '—'}</span></div>
        <div className="pv-fact"><b>Relationship Expectation</b><span>{pr.looking || '—'}</span></div>
      </div>
    )}
    {hasPrefs && pr.interests?.length > 0 && (
      <div className="pv-section"><h4>Interests</h4><div className="ttags">{pr.interests.map((i) => <span className="ttag" key={i}>{i}</span>)}</div></div>
    )}
    {hasPrefs && pr.lifestyle?.length > 0 && (
      <div className="pv-section"><h4>Lifestyle</h4><div className="ttags">{pr.lifestyle.map((i) => <span className="ttag" key={i}>{i}</span>)}</div></div>
    )}
    {hasPrefs && pr.personality?.length > 0 && (
      <div className="pv-section"><h4>Personality</h4><div className="ttags">{pr.personality.map((i) => <span className="ttag" key={i}>{i}</span>)}</div></div>
    )}
    {hasPrefs && pr.notes && (
      <div className="pv-section"><h4>Anything else</h4><p className="pv-about" style={{ margin: 0 }}>{pr.notes}</p></div>
    )}
  </div>
);

export const PreferencesTab = ({ me, setMe, flash, startEdit }) => {
  const pr = me.preferences || {};
  const hasPrefs = Object.keys(pr).length > 0;
  const [mode, setMode] = useState(startEdit ? 'edit' : 'view');
  const [f, setF] = useState(buildForm(pr));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/user/preferences`, {
        age_min: parseInt(f.age_min, 10) || 18,
        age_max: parseInt(f.age_max, 10) || 99,
        cities: f.cities.split(',').map((c) => c.trim()).filter(Boolean),
        looking: f.looking || null,
        interests: f.interests, lifestyle: f.lifestyle, personality: f.personality,
        notes: f.notes || null,
      }, { headers: authHeaders() });
      setMe(data);
      flash('Matching preferences saved.');
      setMode('view');
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
    setBusy(false);
  };

  const importForm = async () => {
    try {
      const { data } = await axios.post(`${API}/user/import-from-form`, {}, { headers: authHeaders() });
      setF(buildForm(data.preferences || {}));
      setMe(data);
      flash('Preferences imported from your matchmaking form');
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  if (mode === 'view') {
    return <PreferencesView pr={pr} hasPrefs={hasPrefs} onEdit={() => { setF(buildForm(pr)); setMode('edit'); }} />;
  }

  return (
    <div className="dash-card" data-testid="preferences-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <h3 style={{ margin: 0 }}>Edit Matching Preferences</h3>
        <button className="btn btn-ghost btn-sm" onClick={importForm} data-testid="prefs-import-btn"><Download size={14} /> Import from my match form</button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--muted)' }}>
        Same questions as your matchmaking form. Krysha uses these to suggest relevant profiles — no swiping, just your vibe.
      </p>
      {!me.profile_live && (
        <div className="al-info" style={{ display: 'flex', gap: 8, alignItems: 'center' }} data-testid="prefs-private-note">
          <Info size={15} /> Your profile is still Private. Save your preferences, then go live from My Profile to be discoverable.
        </div>
      )}
      <form onSubmit={save} style={{ marginTop: 14 }}>
        <div className="fields">
          <div className="field"><label>Age range — from</label><input type="number" min={18} max={99} value={f.age_min} onChange={(e) => set('age_min', e.target.value)} data-testid="pref-age-min" /></div>
          <div className="field"><label>Age range — to</label><input type="number" min={18} max={99} value={f.age_max} onChange={(e) => set('age_max', e.target.value)} data-testid="pref-age-max" /></div>
          <div className="field full"><label>Preferred location(s)</label>
            <input value={f.cities} onChange={(e) => set('cities', e.target.value)} placeholder="e.g. Mumbai, Pune" data-testid="pref-cities" />
            <span className="field-hint">Separate multiple cities with commas.</span>
          </div>
          <div className="field full"><label>Relationship expectation</label>
            <Radios options={PROFILE_OPTIONS.looking} value={f.looking} onPick={(v) => set('looking', v)} testid="pref-looking" />
          </div>
          <div className="field full"><label>Interests you'd like to share</label>
            <Chips options={PROFILE_OPTIONS.interests} value={f.interests} onToggle={(v) => set('interests', toggle(f.interests, v))} testid="pref-interests" />
          </div>
          <div className="field full"><label>Lifestyle you prefer</label>
            <Chips options={PROFILE_OPTIONS.lifestyle} value={f.lifestyle} onToggle={(v) => set('lifestyle', toggle(f.lifestyle, v))} testid="pref-lifestyle" />
          </div>
          <div className="field full"><label>Personality you connect with</label>
            <Chips options={PROFILE_OPTIONS.personality} value={f.personality} onToggle={(v) => set('personality', toggle(f.personality, v))} testid="pref-personality" />
          </div>
          <div className="field full"><label>Anything else that matters to you</label>
            <textarea value={f.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Values, dealbreakers, what you're hoping to find…" data-testid="pref-notes" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn btn-red btn-sm" type="submit" disabled={busy} data-testid="prefs-save-btn"><Sliders size={14} /> Save Preferences</button>
          {hasPrefs && <button className="btn btn-ghost btn-sm" type="button" onClick={() => setMode('view')} data-testid="prefs-cancel-btn"><X size={14} /> Cancel</button>}
        </div>
      </form>
    </div>
  );
};

export default PreferencesTab;
